import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import {
  Rocket, Search, TrendingUp, Package, DollarSign, Target,
  Zap, ArrowRight, Star, AlertCircle, CheckCircle, XCircle,
  BarChart3, ShoppingBag, Globe, Lightbulb, Layers
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function ProductEnginePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('discovery');
  const [discovering, setDiscovering] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const brandId = profile.member?.brand_id || profile.member?.merchant_id;
      if (brandId) {
        const res = await fetch(`${API}/api/products/engine/dashboard?brand_id=${brandId}`);
        if (res.ok) setDashboard(await res.json());
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load:', error);
      navigate('/login');
    }
  };

  const handleDiscover = async () => {
    const brandId = user?.brand_id || user?.merchant_id;
    if (!brandId) return;
    setDiscovering(true);
    try {
      const res = await fetch(`${API}/api/products/discover?brand_id=${brandId}`);
      if (res.ok) {
        await loadData();
      }
    } catch (err) {
      console.error('Discovery failed:', err);
    }
    setDiscovering(false);
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const pipeline = dashboard?.opportunity_pipeline || {};
  const launches = dashboard?.active_launches || [];
  const revenue = dashboard?.revenue_overview || {};
  const flywheel = dashboard?.flywheel_status || {};
  const signals = dashboard?.cross_engine_signals || {};

  const getGradeColor = (grade) => {
    if (grade === 'A') return 'text-green-600 bg-green-100';
    if (grade === 'B') return 'text-blue-600 bg-blue-100';
    if (grade === 'C') return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <MerchantHeader user={user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Rocket className="h-6 w-6 text-indigo-600" />
              Product Discovery & Launch Engine
            </h1>
            <p className="text-gray-500 mt-1">
              Discover, validate, launch, and scale — the complete product lifecycle
            </p>
          </div>
          <button
            onClick={handleDiscover}
            disabled={discovering}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {discovering ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {discovering ? 'Scanning...' : 'Discover Opportunities'}
          </button>
        </div>

        {/* Flywheel Status */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
            <Zap className="h-5 w-5 text-indigo-600" />
            Commerce Index Flywheel
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {['Product Discovery', 'Launch', 'Social Engagement', 'Demand Signals', 'Better Rates', 'More Inventory'].map((step, i) => (
              <React.Fragment key={i}>
                <span className={`px-3 py-1.5 rounded-full font-medium ${
                  (flywheel.active_components || []).some(c => step.toLowerCase().includes(c.split('_')[0]))
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-500 border'
                }`}>{step}</span>
                {i < 5 && <ArrowRight className="h-4 w-4 text-indigo-300" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-indigo-700">Demand Score: <strong>{signals.demand_score ?? 'no data'}{signals.demand_score != null ? '/100' : ''}</strong></span>
            <span className="text-indigo-700">NPS: <strong>{signals.current_nps ?? 'no data'}</strong></span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Opportunities</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{pipeline.total_opportunities || 0}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Active Launches</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{launches.length}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Revenue Streams</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{revenue.active_streams || 0}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {revenue.total_revenue == null ? 'no data' : '$' + revenue.total_revenue.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 overflow-x-auto">
          {[
            { id: 'discovery', label: 'Opportunities', icon: Search },
            { id: 'launches', label: 'Active Launches', icon: Rocket },
            { id: 'revenue', label: 'Revenue Streams', icon: DollarSign },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'discovery' && (
          <div className="space-y-4">
            {(pipeline.top_opportunities || []).length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Search className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-600">No opportunities discovered yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Discover Opportunities" to run an AI scan across your social engagement,
                  customer feedback, and market trends
                </p>
              </div>
            ) : (
              (pipeline.top_opportunities || []).map((opp, i) => (
                <div key={i} className="bg-white rounded-xl border p-5 hover:border-indigo-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          opp.score >= 80 ? 'bg-green-100 text-green-700' :
                          opp.score >= 60 ? 'bg-blue-100 text-blue-700' :
                          opp.score >= 40 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          Score: {opp.score}/100
                        </span>
                        <span className="text-xs text-gray-400">
                          via {opp.source?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900">{opp.title}</p>
                      <p className="text-sm text-gray-500 mt-1">{opp.recommendation}</p>
                    </div>
                    <div className="ml-4">
                      {opp.score >= 60 ? (
                        <div className="flex items-center text-green-600">
                          <TrendingUp className="h-5 w-5" />
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-400">
                          <BarChart3 className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'launches' && (
          <div className="space-y-4">
            {launches.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Rocket className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-600">No active launches</p>
                <p className="text-sm text-gray-400 mt-1">
                  Create a launch from a validated opportunity to start the multi-channel orchestration
                </p>
              </div>
            ) : (
              launches.map((launch, i) => (
                <div key={i} className="bg-white rounded-xl border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">{launch.name}</p>
                      <p className="text-sm text-gray-500">{launch.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {launch.launch_grade && (
                        <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${getGradeColor(launch.launch_grade)}`}>
                          {launch.launch_grade}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                        {launch.phase?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Channels</p>
                      <p className="font-medium">{(launch.channels || []).length}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Day 1 Orders</p>
                      <p className="font-medium">{launch.actual_first_day_orders || 0} / {launch.goal_first_day_orders || '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Waitlist</p>
                      <p className="font-medium">{launch.waitlist_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Pre-orders</p>
                      <p className="font-medium">{launch.pre_orders || 0}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border p-6 mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Revenue Stream Summary</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-xl font-bold text-green-600">{revenue.total_revenue == null ? 'no data' : '$' + revenue.total_revenue.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monthly Recurring</p>
                  <p className="text-xl font-bold text-indigo-600">${(revenue.monthly_recurring || 0).toLocaleString()}/mo</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Streams</p>
                  <p className="text-xl font-bold text-gray-900">{revenue.active_streams || 0}</p>
                </div>
              </div>
            </div>

            {/* Revenue Stream Types */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="h-5 w-5 text-indigo-600" />
                Available Revenue Models
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { name: 'Direct Sale', desc: 'One-time product purchase', icon: ShoppingBag },
                  { name: 'Subscription', desc: 'Recurring monthly revenue', icon: DollarSign },
                  { name: 'Cross-Sell', desc: 'Related product recommendations', icon: Package },
                  { name: 'Upsell', desc: 'Premium upgrades post-purchase', icon: TrendingUp },
                  { name: 'Dynamic Pricing', desc: 'Demand-based price optimization', icon: Zap },
                  { name: 'Marketplace', desc: 'Expand to Amazon, Etsy, Walmart', icon: Globe },
                ].map((model, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <model.icon className="h-5 w-5 text-indigo-500" />
                    <div>
                      <p className="font-medium text-sm text-gray-900">{model.name}</p>
                      <p className="text-xs text-gray-500">{model.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
